import { AxiosError } from 'axios';
import React, { useCallback, useState, useEffect } from 'react';
import {
  BodyText,
  Button,
  Checkbox,
  Div,
  Divider,
  Flex,
  GridBag,
  Gutter,
  Input,
  Loader,
  RadioButton,
  Title,
} from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  AdminBundleAcquisitionPrice,
  BundlePrice,
  BundlePriceTypeEnum,
  Material,
  PriceRequestPriceTypesEnum,
  SalesChannel,
  ServiceError,
} from '@cdaas/api-clients/dist/product-service-openapi';
import { debounce } from 'lodash';

import {
  productServiceAdminBundleAcquisitionPricesClient,
  productServiceAdminBundlePricesClient,
  productServiceAdminMaterialsClient,
} from '../../../api-client/product-service-client';
import {
  EditBundleSalesChannelProps,
  EditBundleSalesChannelErrorType,
  VariantsWithMainInfo,
  BundleErrors,
  EditBundleSalesChannelMode,
} from './types';
import { BundleVariant } from './BundleVariant';
import { resetNotification, setNotification } from '../../../store/ui';
import { bundlePriceTypesInUse } from '../../../utils/prices';

const EditBundleSalesChannel = ({
  bundleId,
  salesChannel,
  bundlePrice,
  bundleVariants,
  country,
  onCancelClick,
  onSaveClick,
  mode,
}: EditBundleSalesChannelProps) => {
  const [priceValue, setPriceValue] = useState<string>(bundlePrice?.amount.toString() || '');
  const [priceType, setPriceType] = useState<BundlePriceTypeEnum>(bundlePrice?.type || BundlePriceTypeEnum.Monthly24);
  const [includeInsurance, setIncludeInsurance] = useState<boolean>(bundlePrice?.insured || false);
  const [loadingContent, setLoadingContent] = useState<boolean>(true);
  const [loadingDynamicPrices, setLoadingDynamicPrices] = useState<boolean>(false);
  const [errors, setErrors] = useState<BundleErrors>({});
  const [isBundlePriceValid, setIsBundlePriceValid] = useState<boolean>(
    salesChannel.use_automatic_lease_price || !!bundlePrice?.amount.toString().length,
  );
  const [acquisitionPrices, setAcquisitionPrices] = useState<AdminBundleAcquisitionPrice[]>([]);

  const { t } = useTranslation('bundlesview');
  const { t: productsT } = useTranslation('productsscreen');
  const dispatch = useDispatch();
  const defaultVat = country?.vat_percentage ? country?.vat_percentage.toString() : '0';
  const hasErrors = Object.keys(errors).length > 0;

  const setErrorToVariant = (variantId: number, errorType: EditBundleSalesChannelErrorType) => {
    if (errors[variantId]) {
      errors[variantId].push(errorType);
    } else {
      errors[variantId] = [errorType];
    }
    setErrors(errors);
  };

  const validateMissingMaterialCodes = (salesChannel: SalesChannel, materialCodes: Material[]) => {
    const salesChannelCountryId = salesChannel.country_id;
    for (const variant of bundleVariants) {
      const code = materialCodes.find(material => material.variant_id === variant.id && material.country_id === salesChannelCountryId);
      if (!code) {
        setErrorToVariant(variant.id, EditBundleSalesChannelErrorType.MissingMaterial);
      }
    }
  };

  const validateDefaultTfapPrices = (prices: AdminBundleAcquisitionPrice[]) => {
    for (const variant of bundleVariants) {
      const tfapPrice = prices.find(price => price.variant_id === variant.id);
      if (!tfapPrice) {
        setErrorToVariant(variant.id, EditBundleSalesChannelErrorType.TfapDefault);
      }
    }
  };

  const getSalesPriceFromTeliaFinance = async (
    updatedPriceType: BundlePriceTypeEnum,
    updatedAcquisitionPrices: AdminBundleAcquisitionPrice[],
    updatedIncludeInsurance: boolean,
  ) => {
    if (salesChannel.use_automatic_lease_price) {
      setLoadingDynamicPrices(true);
      try {
        const acquisitionPricesByVariantId: { [key: string]: number } = {};
        updatedAcquisitionPrices.forEach(price => {
          if (price.overridden_amount) {
            acquisitionPricesByVariantId[price.variant_id.toString()] = price.overridden_amount;
          }
        });
        const response = await productServiceAdminBundlePricesClient.fetchBundlePrices(bundleId, {
          insured: updatedIncludeInsurance,
          price_types: [updatedPriceType as unknown as PriceRequestPriceTypesEnum],
          sales_channel_acquisition_prices_by_variant_id: acquisitionPricesByVariantId,
          sales_channel_id: salesChannel.id!,
        });
        setPriceValue(response.data[updatedPriceType].toFixed(2));
        dispatch(resetNotification());
        setIsBundlePriceValid(true);
      } catch (e) {
        const message = ((e as AxiosError)?.response?.data as ServiceError)?.message ?? t('errors.unknownError');
        dispatch(
          setNotification({
            text: t('errors.fetchPrices', { message: message }),
            show: true,
            type: 'error',
          }),
        );
        setIsBundlePriceValid(false);
        setPriceValue('');
      } finally {
        setLoadingDynamicPrices(false);
      }
    }
  };

  useEffect(() => {
    const fetchMaterialCodesByVariants = async (variants: VariantsWithMainInfo[], countryId: number) => {
      const fetchAcquisitionPrices = async (): Promise<AdminBundleAcquisitionPrice[] | null> => {
        if (!salesChannel.uuid) {
          return null;
        }
        try {
          const response = await productServiceAdminBundleAcquisitionPricesClient.getAcquisitionPrices(bundleId, salesChannel.uuid);
          const { prices } = response.data;
          setAcquisitionPrices(prices);
          validateDefaultTfapPrices(prices);
          return prices;
        } catch (error) {
          console.error(error);
          return null;
        }
      };

      try {
        const variantIDs = variants.map(variant => variant.id);
        const response = await productServiceAdminMaterialsClient.getMaterials(variantIDs, countryId);
        const { materials } = response.data;
        validateMissingMaterialCodes(salesChannel, materials);
        return await fetchAcquisitionPrices();
      } catch (error) {
        console.error(error);
      }
    };

    if (mode === EditBundleSalesChannelMode.Edit && bundlePrice) {
      setPriceType(bundlePrice?.type as BundlePriceTypeEnum);
      setPriceValue(bundlePrice.amount.toString());
    }

    const fetchInitialData = async () => {
      try {
        const acquisitionPricesFetched = await fetchMaterialCodesByVariants(bundleVariants, country.id);
        if (salesChannel.use_automatic_lease_price) {
          await getSalesPriceFromTeliaFinance(priceType, acquisitionPricesFetched ?? [], includeInsurance);
        }
      } finally {
        setLoadingContent(false);
      }
    };
    fetchInitialData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePriceOnChange = useCallback((_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setPriceValue(value);
    if (!value || value.length < 1) {
      setIsBundlePriceValid(false);
    } else {
      setIsBundlePriceValid(true);
    }
  }, []);

  const debouncedUpdatePrice = debounce(getSalesPriceFromTeliaFinance, 500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdatePriceCallback = useCallback(debouncedUpdatePrice, []);

  useEffect(() => {
    return () => {
      debouncedUpdatePriceCallback.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTfapPriceOverridenAmountChange = (variant: VariantsWithMainInfo, value: string) => {
    const updatedAcquisitionPrices: AdminBundleAcquisitionPrice[] = acquisitionPrices.map(acquisitionPrice => {
      // When TFAP is overwritten for one variant used in the bundle, the bundle needs to be checked if it uses
      // other variants from the same product as the one with overwritten TFAP. TFAP for all these variants
      // needs to be overwritten to same value. Otherwise bundle might have different price based on what variant customer chooses.
      const productId = variant.product_id;
      const variantsOfProduct = bundleVariants.filter(variant => variant.product_id === productId).map(variant => variant.id);
      return variantsOfProduct.includes(acquisitionPrice.variant_id)
        ? value
          ? {
              ...acquisitionPrice,
              overridden_amount: Number(value),
            }
          : {
              default_amount: acquisitionPrice.default_amount,
              variant_id: acquisitionPrice.variant_id,
            }
        : acquisitionPrice;
    });
    setAcquisitionPrices(updatedAcquisitionPrices);
    debouncedUpdatePriceCallback(priceType, updatedAcquisitionPrices, includeInsurance);
  };

  const handlePriceTypeOnChange = (event: React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLDivElement>) => {
    const value = (event as React.ChangeEvent<HTMLInputElement>).target.value as BundlePriceTypeEnum;
    setPriceType(value);
    debouncedUpdatePriceCallback(value, acquisitionPrices, includeInsurance);
  };

  const sortVariantsByMainProduct = bundleVariants.sort(
    (variantA: VariantsWithMainInfo, variantB: VariantsWithMainInfo) => Number(variantB.isMain) - Number(variantA.isMain),
  );

  const saveBundleToChannel = () => {
    const newBundlePrice: BundlePrice = {
      amount: Number(priceValue),
      id: bundlePrice?.id || 0,
      sales_channel_uuid: salesChannel.uuid || '',
      type: priceType,
      insured: salesChannel.use_automatic_lease_price ? includeInsurance : false,
      vat_percentage: Number(defaultVat),
    };
    // Sales channel has id as it is fetched through admin end point
    onSaveClick(newBundlePrice, acquisitionPrices, mode, salesChannel.id!);
  };

  const handleInsuranceToggle = (checked: boolean) => {
    setIncludeInsurance(checked);
    getSalesPriceFromTeliaFinance(priceType, acquisitionPrices, checked);
  };
  return (
    <Div padding={{ left: 'md', top: 'lg', right: 'md' }}>
      <Title size="xxs">{t('editSalesChannels.acquisionPriceTitle')}</Title>
      <Gutter />
      <BodyText size="md">{t('editSalesChannels.overrideDefaultPriceInfoText1')}</BodyText>
      <BodyText size="md">{t('editSalesChannels.overrideDefaultPriceInfoText2')}</BodyText>
      <Gutter size="md" />
      <GridBag>
        <GridBag.Item md={6}>{t('editSalesChannels.productVariantLabel')}</GridBag.Item>
        <GridBag.Item md={3}>{t('editSalesChannels.defaultTFapLabel', { currency: country.currency })}</GridBag.Item>
        <GridBag.Item md={3}>{t('editSalesChannels.overrideTFapLabel', { currency: country.currency })}</GridBag.Item>
      </GridBag>
      {!loadingContent &&
        sortVariantsByMainProduct.map(variant => (
          <BundleVariant
            key={`bundle-variant-${variant.id}`}
            variant={variant}
            country={country}
            acquisitionPrices={acquisitionPrices}
            errors={errors}
            onTfapPriceOverridenAmountChange={handleTfapPriceOverridenAmountChange}
          />
        ))}
      {loadingContent && (
        <Div data-testid={'loading'} alignTextToCenter>
          <Loader flexItem size="md" />
        </Div>
      )}
      <Gutter size="sm" />
      <Divider />
      <Gutter size="sm" />
      <Title size="xxs">{t('editSalesChannels.bundleLevelSettingsLabel')}</Title>
      {/* Insurance checkbox should only be shown for sales channels where new automatic pricing is in use
      because it only affects prices in said sales channels. For sales channels using the old
      manual pricing this checkbox should not be shown and insurance's value should be default false. */}
      {salesChannel.use_automatic_lease_price && (
        <Checkbox
          label={t('editSalesChannels.includesInsuranceLabel')}
          padding={{ top: 'sm' }}
          checked={includeInsurance}
          onChange={(_event, checked) => handleInsuranceToggle(checked)}
        />
      )}
      <Gutter size="sm" />
      <Title size="xxs">{t('editSalesChannels.paymentOptionLabel')}</Title>
      {bundlePriceTypesInUse.map(priceTypeEnum => {
        return (
          <RadioButton
            block
            key={priceTypeEnum[1]}
            onChange={handlePriceTypeOnChange}
            label={productsT(`priceTypes.${priceTypeEnum[1]}`)}
            padding={{ top: 'sm' }}
            value={priceTypeEnum[1]}
            checked={priceTypeEnum[1] === priceType}
          />
        );
      })}
      <Gutter size="sm" />
      <GridBag>
        <GridBag.Item md={4}>
          {loadingDynamicPrices ? (
            <Div data-testid={'loading-prices'} alignTextToCenter>
              <Loader flexItem size="md" />
            </Div>
          ) : (
            <Flex alignBottom>
              <Input
                required={!salesChannel.use_automatic_lease_price}
                disabled={salesChannel.use_automatic_lease_price}
                // @ts-expect-error Type 'number' is not assignable to type 'text' | 'search' | 'tel' | 'url' | 'email' | 'password' | undefined
                type={'number'}
                min={0}
                step={'.01'}
                placeholder={country.currency.toUpperCase()}
                aria-label={`${productsT('sales_channel_drawer.salesPrice', { currency: country.currency })}`}
                label={`${productsT('sales_channel_drawer.salesPrice', { currency: country.currency })}`}
                value={priceValue}
                onChange={handlePriceOnChange}
                data-testid="sales-price"
              />
            </Flex>
          )}
        </GridBag.Item>
        <GridBag.Item md={3}>
          {!salesChannel.use_automatic_lease_price && (
            <Flex alignBottom>
              <Input
                disabled={true} // Show VAT input as a read only field
                // @ts-expect-error Type 'number' is not assignable to type 'text' | 'search' | 'tel' | 'url' | 'email' | 'password' | undefined
                type={'number'}
                min={0}
                step={'.01'}
                aria-label={productsT('sales_channel_drawer.vatLabel')}
                label={productsT('sales_channel_drawer.vatLabel')}
                value={defaultVat}
                placeholder={productsT('sales_channel_drawer.vatInputPlaceholder').toUpperCase()}
              />
            </Flex>
          )}
        </GridBag.Item>
      </GridBag>
      <Gutter size="md" />
      <Divider />
      <Gutter size="sm" />
      <GridBag>
        <GridBag.Item md={3}>
          <Button
            disabled={loadingContent || hasErrors || !isBundlePriceValid || loadingDynamicPrices}
            onClick={() => saveBundleToChannel()}
            variant="primary"
          >
            {mode === EditBundleSalesChannelMode.Edit ? t('editSalesChannels.saveChanges') : t('editSalesChannels.addToChannel')}
          </Button>
        </GridBag.Item>
        <GridBag.Item md={3}>
          <Button variant="secondary" onClick={() => onCancelClick(salesChannel.id!)}>
            {t('editSalesChannels.cancel')}
          </Button>
        </GridBag.Item>
      </GridBag>
      <Gutter size="sm" />
    </Div>
  );
};

export default EditBundleSalesChannel;
